import {
  getLocalTimeZone,
  now,
  toCalendarDate,
  toCalendarDateTime,
} from "@internationalized/date";
import { CalendarIcon } from "lucide-react";
import React from "react";
import {
  type DatePickerProps,
  DatePickerStateContext,
  type DateValue,
  DatePicker as RACDatePicker,
} from "react-aria-components";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { DateInput } from "@/components/ui/date-field";
import { Dialog } from "@/components/ui/dialog";
import { FieldGroup } from "@/components/ui/field";
import { Popover } from "@/components/ui/popover";
import { composeTailwindRenderProps } from "@/components/ui/utils";

export function DatePicker<T extends DateValue>({
  hourCycle = 24,
  ...props
}: DatePickerProps<T>) {
  return (
    <RACDatePicker
      {...props}
      hourCycle={hourCycle}
      className={composeTailwindRenderProps(
        props.className,
        "group flex flex-col gap-1",
      )}
    />
  );
}

export function DatePickerInput({ className }: { className?: string }) {
  const { onKeyDown } = useDatePickerShortcuts();
  return (
    <>
      <FieldGroup className={className} onKeyDown={onKeyDown}>
        <DateInput className="flex-1 px-2" />
        <Button
          variant="ghost"
          size="icon-sm"
          excludeFromTabOrder
          className="mr-1 rounded text-muted-foreground"
        >
          <CalendarIcon aria-hidden className="w-4 h-4" />
        </Button>
      </FieldGroup>
      <Popover className="p-2 flex min-w-auto snap-x justify-center">
        <Dialog className="overflow-visible" hideCloseButton>
          <Calendar />
        </Dialog>
      </Popover>
    </>
  );
}

function useDatePickerShortcuts() {
  const ctx = React.useContext(DatePickerStateContext);

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const key = e.key.toLowerCase();
      if (key === "t" || key === "y") {
        e.stopPropagation();
        let nowValue = now(getLocalTimeZone());
        if (key === "y") {
          nowValue = nowValue.subtract({ days: 1 });
        }
        // only ever need minutes precision
        nowValue = nowValue.set({ second: 0, millisecond: 0 });
        ctx?.setValue(
          ctx.hasTime ? toCalendarDateTime(nowValue) : toCalendarDate(nowValue),
        );
      }
    },
    [ctx?.hasTime, ctx?.setValue],
  );

  return { onKeyDown };
}
